// Copyright 1999-2020. Plesk International GmbH. All rights reserved.

import { createElement } from 'react';
import PropTypes from 'prop-types';
import { Translate, Paragraph, FormFieldRadioButtons } from '@plesk/ui-library';
import DrawerAdapter from './DrawerAdapter';
import { PLESK_COOKIES, CONTROL_COOKIES } from '../urls';

const toValues = preferences =>
    Object.keys(preferences).reduce((acc, k) => {
        acc[k] = preferences[k] ? 'on' : 'off';
        return acc;
    }, {});

const toPreferences = values =>
    Object.keys(values).reduce((acc, k) => {
        acc[k] = values[k] === 'on';
        return acc;
    }, {});

const Preferences = ({ isOpen, onSave, onToggle, preferences, adapter: Adapter = DrawerAdapter }) => {
    const namespace = 'preferences';

    const handleSubmit = values => {
        onSave(toPreferences(values));
        onToggle(false);
    };

    const handleCancel = () => onToggle(false);
    const values = toValues(preferences);

    return (
        <Adapter
            isOpen={isOpen}
            title={<Translate content={`${namespace}.title`} />}
            submitButton={{
                children: (
                    <Translate
                        content={`${namespace}.submitButton`}
                        data-type="cookie-policy-preferences-submit-button"
                    />
                ),
            }}
            cancelButton={{
                children: (
                    <Translate
                        content={`${namespace}.cancelButton`}
                        data-type="cookie-policy-preferences-cancel-button"
                    />
                ),
                onClick: handleCancel,
            }}
            values={values}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
        >
            <Paragraph>
                <Translate content={`${namespace}.description`} />
            </Paragraph>
            <h2>
                <Translate content={`${namespace}.settingsTitle`} />
            </h2>
            <Paragraph>
                <Translate content={`${namespace}.settingsDescription`} />
            </Paragraph>
            <h3>
                <Translate content={`${namespace}.analyticsTitle`} />
            </h3>
            <Paragraph>
                <Translate content={`${namespace}.analyticsDescription`} />
            </Paragraph>
            <FormFieldRadioButtons
                name="analytics"
                value={values.analytics}
                className="cookie-policy-preferences__radio-buttons"
                options={[
                    {
                        value: 'on',
                        label: <Translate content={`${namespace}.on`} />,
                    },
                    {
                        value: 'off',
                        label: <Translate content={`${namespace}.off`} />,
                    },
                ]}
            />
            <h3>
                <Translate content={`${namespace}.marketingTitle`} />
            </h3>
            <Paragraph>
                <Translate content={`${namespace}.marketingDescription`} />
            </Paragraph>
            <FormFieldRadioButtons
                name="marketing"
                value={values.marketing}
                className="cookie-policy-preferences__radio-buttons"
                options={[
                    {
                        value: 'on',
                        label: <Translate content={`${namespace}.on`} />,
                    },
                    {
                        value: 'off',
                        label: <Translate content={`${namespace}.off`} />,
                    },
                ]}
            />
            <h3>
                <Translate content={`${namespace}.necessaryTitle`} />
            </h3>
            <Paragraph>
                <Translate
                    content={`${namespace}.necessaryDescription`}
                    params={{
                        browserSettingsLink: (
                            <a href={CONTROL_COOKIES} target="_blank" rel="noopener noreferrer">
                                <Translate content={`${namespace}.browserSettingsLink`} />
                            </a>
                        ),
                    }}
                />
            </Paragraph>
            <Paragraph>
                <a href={PLESK_COOKIES} target="_blank" rel="noopener noreferrer">
                    <Translate content={`${namespace}.pleskCookiesLink`} />
                </a>
            </Paragraph>
        </Adapter>
    );
};

Preferences.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onSave: PropTypes.func.isRequired,
    onToggle: PropTypes.func.isRequired,
    preferences: PropTypes.object.isRequired,
    adapter: PropTypes.func,
};

Preferences.defaultProps = {
    adapter: undefined,
};

export default Preferences;



// WEBPACK FOOTER //
// ./app/cookie-policy/Preferences/Preferences.js